import React, {useCallback, useRef, useState} from 'react';
import {Badge, Button, Tabs, Tag, Tooltip} from 'antd';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {EditOutlined, SaveOutlined} from '@ant-design/icons';
import {useTranslation} from '../../../hooks/useTranslation';
import {useIssueDetail} from '../../../hooks/issues/useIssues';
import IssueDetailNavigation from './navigation/IssueDetailNavigation';
import {IssueFormActions} from './panels/IssueForm';
import {useSetBreadcrumb} from '../../../contexts/BreadcrumbContext';
import BootstrapIcon from "../../../components/common/BootstrapIcon";
import PageContainer from "../../../components/common/PageContainer";
import PrimaryButton from "../../../components/common/PrimaryButton";
import '../issues.less';

const IssueDetail: React.FC = () => {
  const {id} = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {t} = useTranslation();

  const isEditMode = !!id && id !== 'new';
  const {data: issueData} = useIssueDetail(isEditMode ? id : undefined);
  const issueCode = issueData?.codeIncident;
  const codeIssue = issueCode || (id === 'new' ? 'new' : '—');

  const issueTitle = issueData?.title;
  const headerTitle = (
    <span className="issue-detail-title">
      {isEditMode && issueCode && (
        <Tag color="blue" variant={"outlined"}>{issueCode}</Tag>
      )}
      <span className="issue-detail-title__text" title={issueTitle}>
        {issueTitle || (isEditMode ? '' : t('issues.detail.header.newTitle'))}
      </span>
    </span>
  );

  const activePanel = searchParams.get('panel') || 'issue-form';


  const correlativeParam = searchParams.get('correlative');
  const issueCorrelative = correlativeParam ? Number(correlativeParam) : undefined;

  // Show the issue code (not the raw route id) in the header breadcrumb
  useSetBreadcrumb([
    {label: 'Giro', to: '/'},
    {label: t('navigation.modules.incidents.title'), to: '/issues'},
    {label: codeIssue},
  ]);

  // Imperative handle to trigger save on the issue form from the page header
  const formActionsRef = useRef<IssueFormActions | null>(null);
  const [headerSaving, setHeaderSaving] = useState(false);

  const handleRegisterActions = useCallback((actions: IssueFormActions) => {
    formActionsRef.current = actions;
  }, []);

  const handlePanelChange = (panelKey: string) => {
    // Preserve other params (e.g. correlative) when switching panels.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('panel', panelKey);
      return next;
    });
  };

  const handleBackToIssues = () => {
    navigate('/issues');
  };

  const handleSaveIssue = () => {
    formActionsRef.current?.submit();
  };

  const navigationOptions = [
    {
      key: 'issue-form',
      icon: <EditOutlined/>,
      titleKey: 'issues.detail.navigation.issueDetails.title',
      subtitleKey: 'issues.detail.navigation.issueDetails.subtitle',
    },
    {
      key: 'issue-impact-assessment',
      icon: <BootstrapIcon icon={"speedometer"}/>,
      titleKey: 'issues.detail.navigation.impactAssessment.title',
      subtitleKey: 'issues.detail.navigation.impactAssessment.subtitle',
    },
    {
      key: 'issue-approval',
      icon: <BootstrapIcon icon={"person-check"}/>,
      titleKey: 'issues.detail.navigation.approval.title',
      subtitleKey: 'issues.detail.navigation.approval.subtitle',
    },
    {
      key: 'issue-monitoring',
      icon: <BootstrapIcon icon={"stack"}/>,
      titleKey: 'issues.detail.navigation.monitoring.title',
      subtitleKey: 'issues.detail.navigation.monitoring.subtitle',
    }
  ];

  const isIssueForm = activePanel === 'issue-form';
  const isImpactAssessment = activePanel === 'issue-impact-assessment';
  const showHeaderActions = isIssueForm || isImpactAssessment;
  const saveLabel = isImpactAssessment
    ? t('issues.impactAssessment.saveAssessment')
    : t('issues.saveIssue');

  return (
    <PageContainer
      className="issue-detail-page"
      title={headerTitle}
      // subTitle={t('issues.detail.header.subtitle')}
      titleClassName="issue-detail-page__title"
      bordered={false}
      extra={
        showHeaderActions ? (
          <>
            <Button onClick={handleBackToIssues}>
              {t('issues.detail.header.cancel')}
            </Button>
            <PrimaryButton
              type="primary"
              icon={<SaveOutlined/>}
              loading={headerSaving}
              onClick={handleSaveIssue}
            >
              {saveLabel}
            </PrimaryButton>
          </>
        ) : undefined
      }
      tabs={
        <Tabs
          className="gt-issue-tabs"
          activeKey={activePanel}
          onChange={handlePanelChange}
          type="line"
          size="middle"
          tabBarStyle={{margin: 0}}
          items={navigationOptions.map((option) => ({
            key: option.key,
            label: (
              <Tooltip title={t(option.subtitleKey)} placement="bottom">
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                  {option.icon}
                  <span>{t(option.titleKey)}</span>
                </span>
              </Tooltip>
            )
          }))}
        />
      }
    >
      <div style={{flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden'}}>
        <IssueDetailNavigation
          issueId={id}
          issueCorrelative={issueCorrelative}
          activePanel={activePanel}
          onRegisterActions={handleRegisterActions}
          onSavingChange={setHeaderSaving}
        />
      </div>
    </PageContainer>
  );
};

export default IssueDetail;
